import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";

const LINKS = [
  { id: "inicio", label: "Início" },
  { id: "sobre", label: "Sobre" },
  {
    id: "servicos",
    label: "Serviços",
    dropdown: [
      { id: "servicos", label: "Soluções" },
      { id: "diferenciais", label: "Diferenciais" },
      { id: "clientes", label: "Clientes" },
    ],
  },
  { id: "contato", label: "Contato" },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  // Monitora o scroll para aplicar o background sólido e shadow no header
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Bloqueia o scroll do body quando o menu mobile está ativo (Prevenção de layout shift)
  useEffect(() => {
    if (mobileMenuOpen) {
      const scrollBarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;
      document.documentElement.style.overflowX = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflowX = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }
  }, [mobileMenuOpen]);

  // Navegação suave com offset para compensar a altura do header
  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    setMobileDropdownOpen(false);

    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        const yOffset = -80;
        const y =
          element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 100);
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-[100] transition-all duration-500 ${
          isScrolled
            ? "bg-[#192338] shadow-[0_10px_40px_rgba(0,0,0,0.4)]"
            : "bg-gradient-to-b from-[#192338] via-[#192338]/40 to-transparent"
        }`}
      >
        {/* Linha de gradiente inferior: Ativada apenas no scroll para acabamento premium */}
        <div
          className={`absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#DDC385]/40 to-transparent transition-opacity duration-500 ${isScrolled ? "opacity-100" : "opacity-0"}`}
        />

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative">
          <div className="flex items-center justify-between h-20">
            {/* Logo APS: Identidade visual com borda e linha de gradiente interna */}
            <button
              onClick={() => scrollToSection("inicio")}
              className="group relative flex items-center bg-transparent border-none cursor-pointer outline-none text-left z-[110]"
            >
              <div className="relative w-12 h-12 bg-[#192338] border border-[#DDC385]/40 flex items-center justify-center transition-all duration-500 group-hover:border-[#DDC385] shadow-xl">
                <div className="flex flex-col items-center">
                  <span className="text-[#DDC385] font-serif font-bold text-lg leading-none tracking-tighter">
                    APS
                  </span>
                  <div className="w-8 h-[1px] bg-gradient-to-r from-transparent via-[#DDC385] to-transparent mt-0.5 opacity-70" />
                </div>
              </div>
              <div className="ml-3 flex flex-col items-start border-l border-white/10 pl-3">
                <span className="text-[10px] tracking-[0.4em] text-white/50 uppercase font-light">
                  Administrativo
                </span>
                <span className="text-[8px] tracking-[0.2em] text-[#DDC385]/70 uppercase">
                  Premium Management
                </span>
              </div>
            </button>

            {/* Navegação Desktop: Links com hover animado em linha de base */}
            <nav className="hidden md:flex items-center gap-8">
              {LINKS.map((link) => (
                <div
                  key={link.id}
                  className="relative flex items-center h-20"
                  onMouseEnter={() => link.dropdown && setDropdownOpen(true)}
                  onMouseLeave={() => link.dropdown && setDropdownOpen(false)}
                >
                  <button
                    onClick={() => !link.dropdown && scrollToSection(link.id)}
                    className="group relative py-2 text-[15px] text-white font-medium transition-all duration-300 hover:text-[#DDC385] flex items-center gap-1.5 outline-none"
                  >
                    {link.label}
                    {link.dropdown && (
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-300 ${dropdownOpen ? "rotate-180 text-[#DDC385]" : "opacity-40"}`}
                      />
                    )}
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-[#DDC385] transition-all duration-500 group-hover:w-full" />
                  </button>

                  {/* Dropdown: Design com bordas em gradiente e fundo de baixa opacidade */}
                  <AnimatePresence>
                    {link.dropdown && dropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 15, x: "-50%" }}
                        animate={{ opacity: 1, y: 0, x: "-50%" }}
                        exit={{ opacity: 0, y: 10, x: "-50%" }}
                        className="absolute top-[85%] left-1/2 -translate-x-1/2 w-64 bg-[#192338] border border-[#DDC385]/30 shadow-[0_30px_60px_rgba(0,0,0,0.8)] p-1 overflow-hidden"
                      >
                        <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-[#DDC385]/60 to-transparent" />
                        <div className="flex flex-col py-1">
                          {link.dropdown.map((sub) => (
                            <button
                              key={sub.id}
                              onClick={() => scrollToSection(sub.id)}
                              className="group/item relative w-full text-left px-5 py-4 transition-all duration-300"
                            >
                              <span className="relative z-10 text-[11px] tracking-[0.3em] uppercase text-white/60 group-hover/item:text-white transition-all duration-300">
                                {sub.label}
                              </span>
                              <div className="absolute inset-0 bg-white/5 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                            </button>
                          ))}
                        </div>
                        <div className="h-px w-full bg-gradient-to-r from-transparent via-[#DDC385]/30 to-transparent" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </nav>

            {/* Toggle Mobile: Ícone de menu responsivo */}
            <button
              className="md:hidden text-white p-2 z-[110] outline-none"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={32} /> : <Menu size={32} />}
            </button>
          </div>
        </div>
      </header>

      {/* Menu Mobile: Overlay em tela cheia com animação lateral */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed top-0 right-0 bottom-0 w-full bg-[#192338] z-[95] md:hidden flex flex-col pt-24 px-8 overflow-y-auto"
          >
            <nav className="flex flex-col gap-2 relative z-[96]">
              {LINKS.map((link) => (
                <div
                  key={link.id}
                  className="flex flex-col border-b border-white/5"
                >
                  {link.dropdown ? (
                    <>
                      <button
                        onClick={() =>
                          setMobileDropdownOpen(!mobileDropdownOpen)
                        }
                        className="flex items-center justify-between text-white text-xl py-6 font-medium"
                      >
                        {link.label}
                        <ChevronDown
                          size={22}
                          className={`${mobileDropdownOpen ? "rotate-180" : ""} text-[#DDC385]`}
                        />
                      </button>
                      {/* Sublinks Mobile com tipografia tracking largo */}
                      {mobileDropdownOpen && (
                        <div className="bg-white/5 rounded-lg mb-4">
                          {link.dropdown.map((sub) => (
                            <button
                              key={sub.id}
                              onClick={() => scrollToSection(sub.id)}
                              className="w-full text-left px-6 py-5 text-[11px] tracking-[0.3em] uppercase text-[#DDC385]"
                            >
                              {sub.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => scrollToSection(link.id)}
                      className="text-white text-xl py-6 text-left font-medium"
                    >
                      {link.label}
                    </button>
                  )}
                </div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
